import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { useMember } from "@/contexts/MemberAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Membership() {
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState("");
  const { member, memberApi } = useMember();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const canceled = params.get("canceled");

  useEffect(() => {
    axios.get(`${API}/tiers`).then((r) => setTiers(r.data || []));
  }, []);

  const choose = async (slug) => {
    if (!member) { nav(`/join?tier=${slug}`); return; }
    setBusy(slug);
    try {
      const { data } = await memberApi.post("/member/subscribe", { tier_slug: slug, origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
      if (data.free) { nav("/account"); return; }
    } catch (e) { alert(e.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-28 pb-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[10px] tracking-[0.4em] text-[#D4AF37] mb-4">MEMBERSHIP · 3RD DEGREE ENT.</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-4">
            Join The <span className="text-[#D4AF37]">Movement</span>
          </h1>
          <p className="text-white/50 max-w-xl mx-auto text-sm leading-relaxed">
            Unlock exclusive drops, early access, private streams, and direct connection.
            Every tier is a deeper step into the world of Luxifer 6.
          </p>
          {canceled && <p className="mt-6 text-xs text-red-400/80">Checkout canceled. Try again anytime.</p>}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((t) => {
            const featured = t.slug === "black";
            return (
              <div
                key={t.slug}
                data-testid={`tier-card-${t.slug}`}
                className={`relative rounded-lg border p-6 flex flex-col transition-all duration-300 ${
                  featured
                    ? "border-[#D4AF37] bg-gradient-to-b from-[#D4AF37]/5 to-transparent scale-[1.02]"
                    : "border-white/5 bg-[#0A0A0A]"
                }`}
              >
                {featured && (
                  <span className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#D4AF37] text-black text-[10px] tracking-widest font-bold rounded">
                    MOST POPULAR
                  </span>
                )}
                <div className="text-2xl mb-3" style={{ color: t.color || "#D4AF37" }}>{t.badge_emoji || "◆"}</div>
                <h3 className="font-heading text-2xl text-white tracking-wider mb-1">{t.name}</h3>
                <div className="mb-5">
                  <span className="text-3xl text-white font-bold">${(t.price || 0).toFixed(2)}</span>
                  <span className="text-white/40 text-xs ml-1">/{t.interval || "month"}</span>
                </div>
                <ul className="space-y-2 flex-1 mb-6">
                  {(t.benefits || []).map((b, i) => (
                    <li key={i} className="flex gap-2 text-xs text-white/60">
                      <Check className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => choose(t.slug)}
                  disabled={busy === t.slug}
                  data-testid={`tier-select-${t.slug}`}
                  className={`w-full py-3 text-xs uppercase tracking-widest transition-all ${
                    featured
                      ? "bg-[#D4AF37] text-black hover:bg-white"
                      : "border border-white/20 text-white hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  } disabled:opacity-40`}
                >
                  {busy === t.slug ? "Loading..." : t.price > 0 ? "Subscribe" : "Get Started"}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center text-xs text-white/30">
          Already a member? <Link to="/login" className="text-[#D4AF37] hover:text-white" data-testid="member-login-link">Sign In</Link>
          <span className="mx-3">·</span>
          <Sparkles className="w-3 h-3 inline text-[#D4AF37]" /> Cancel anytime through your account portal.
        </div>
      </div>
    </div>
  );
}
